/**
 * @module envVars
 * @description These are Environment variables used in Netlify dashboard to configure the web app.
 */

/**
 * @name REACT_APP_REMOVE_FCL_45_HC_CONTAINER
 * @const {string}
 * @default NO
 *
 * @description Whether to remove 45' HC from fcl container. Either use "YES" or "NO".
 * @example REACT_APP_REMOVE_FCL_45_HC_CONTAINER = YES
 */
export const REMOVE_FCL_45_HC_CONTAINER =
  process.env.REACT_APP_REMOVE_FCL_45_HC_CONTAINER.toLowerCase() === 'yes';

/**
 * @name REACT_APP_FCL_45_HC_FALSY_VALUE_MULTIPLIER
 * @const {number}
 *
 * @description if property '45hq' for FCL, is zero/falsy in rate json, then 45 hq is (this env. var * fcl 40Hq Rate (property '40hq' in rate json) ).
 * @see {@link module:util.checkFcl45hqPrice}
 * @example REACT_APP_FCL_45_HC_FALSY_VALUE_MULTIPLIER = 10
 */
export const FCL_45_HC_FALSY_VALUE_MULTIPLIER =
  process.env.REACT_APP_FCL_45_HC_FALSY_VALUE_MULTIPLIER;

/**
 * @name REACT_APP_G_MAP_API_KEY
 * @const {string}
 *
 * @description Google Map API key.
 * @example REACT_APP_G_MAP_API_KEY = AIzadgdgddfdfR4LQ-Zgdgr
 */
export const G_MAP_API_KEY = process.env.REACT_APP_G_MAP_API_KEY;

/**
 * @name REACT_APP_SHOW_CUSTOM_PORTS_DROPDOWN
 * @const {string}
 *
 * @description Whether to show Custom ports dropdown on Search bar page.
 * Non-custom ports dropdown is the Google map dropdown.
 * Either use "YES" or "NO"
 * @example REACT_APP_SHOW_CUSTOM_PORTS_DROPDOWN = YES
 */
export const SHOW_CUSTOM_PORTS_DROPDOWN =
  process.env.REACT_APP_SHOW_CUSTOM_PORTS_DROPDOWN;

/**
 * @name REACT_APP_SEARCH_PORT_ORIGIN_URL
 * @const {string}
 *
 * @description It's the URL for custom ports dropdown, get remote origin ports on search bar page.
 * @example REACT_APP_SEARCH_PORT_ORIGIN_URL = https://primefreight.bettywebblocks.com/api/rates?type=ports
 */
export const SEARCH_PORT_ORIGIN_URL =
  process.env.REACT_APP_SEARCH_PORT_ORIGIN_URL;

/**
 * @name REACT_APP_SEARCH_PORT_DESTINATION_URL
 * @const {string}
 *
 * @description It's the URL for custom ports dropdown, get remote destination ports on search bar page.
 * @example REACT_APP_SEARCH_PORT_DESTINATION_URL = https://primefreight.bettywebblocks.com/api/rates?type=ports
 */

export const SEARCH_PORT_DESTINATION_URL =
  process.env.REACT_APP_SEARCH_PORT_DESTINATION_URL;

/**
 * @name REACT_APP_API_KEY
 * @const {string}
 *
 * @description API key.
 * @example REACT_APP_API_KEY = TEST
 */

export const API_KEY = process.env.REACT_APP_API_KEY;

/**
 * @name REACT_APP_AUTH_TOKEN
 * @const {string}
 *
 * @description Auth token.
 * @example REACT_APP_AUTH_TOKEN = 5026
 */
export const AUTH_TOKEN = process.env.REACT_APP_AUTH_TOKEN;

/**
 * @name REACT_APP_SEARCH_BAR_URL
 * @const {string}
 *
 * @description It's the URL to get results cards based on input data upon click on Search button on search bar page. It's used for FCL, LCL & AIR types
 * @example REACT_APP_SEARCH_BAR_URL = http://httpbin.org/post
 */
export const SEARCH_BAR_URL = process.env.REACT_APP_SEARCH_BAR_URL;

/**
 * @name REACT_APP_SEARCH_CONTACT_FORM_URL
 * @const {string}
 *
 * @description It's the URL for Contact form submit on top of all results cards on Search bar page.
 * @example REACT_APP_SEARCH_CONTACT_FORM_URL = http://httpbin.org/post
 */
export const SEARCH_CONTACT_FORM_URL =
  process.env.REACT_APP_SEARCH_CONTACT_FORM_URL;

/**
 * @name REACT_APP_BOOK_NOW_URL
 * @const {string}
 *
 * @description It's the URL for Book Now, when Book Now button is clicked on results Card on Search bar page.
 * @example REACT_APP_BOOK_NOW_URL = http://httpbin.org/post
 */
export const BOOK_NOW_URL = process.env.REACT_APP_BOOK_NOW_URL;

/**
 * @name REACT_APP_CHAT_URL
 * @const {string}
 *
 * @description It's the URL for Chats, when Chat button is clicked on results Card on Search bar page.
 * @example REACT_APP_CHAT_URL = http://httpbin.org/post
 */
export const CHAT_URL = process.env.REACT_APP_CHAT_URL;

/**
 * @name REACT_APP_SESSION_JSON_URL
 * @const {string}
 *
 * @example REACT_APP_SESSION_JSON_URL = https://primefreight.bettywebblocks.com/temptes?type=usercheck
 */
export const SESSION_JSON_URL = process.env.REACT_APP_SESSION_JSON_URL;

/**
 * @name REACT_APP_SAVE_QUOTE_URL
 * @const {string}
 *
 * @description It's the URL for Save Quote, when Save Quote button is clicked on results Card on Search bar page.
 * @example REACT_APP_SAVE_QUOTE_URL = http://httpbin.org/post
 */
export const SAVE_QUOTE_URL = process.env.REACT_APP_SAVE_QUOTE_URL;

/**
 * @name REACT_APP_SAILING_SCHEDULE_POST_URL
 * @const {string}
 *
 * @description It's the URL for Sailing Schedule, when Sailing Schedule link text is clicked on results Card on Search bar page.
 * @example REACT_APP_SAILING_SCHEDULE_POST_URL = http://httpbin.org/post
 */
export const SAILING_SCHEDULE_POST_URL =
  process.env.REACT_APP_SAILING_SCHEDULE_POST_URL;

/**
 * @name REACT_APP_FCL_API_BASE_URL
 * @const {string}
 *
 * @description It's the URL for getting rate json for FCL.
 * @example REACT_APP_FCL_API_BASE_URL = http://httpbin.org/get
 */
export const FCL_API_BASE_URL = process.env.REACT_APP_FCL_API_BASE_URL;

/**
 * @name REACT_APP_LCL_API_BASE_URL
 * @const {string}
 *
 * @description It's the URL for getting rate json for LCL.
 * @example REACT_APP_LCL_API_BASE_URL = http://httpbin.org/get
 */
export const LCL_API_BASE_URL = process.env.REACT_APP_LCL_API_BASE_URL;

/**
 * @name REACT_APP_AIR_API_BASE_URL
 * @const {string}
 *
 * @description It's the URL for getting rate json for AIR.
 * @example REACT_APP_AIR_API_BASE_URL = http://httpbin.org/get
 */
export const AIR_API_BASE_URL = process.env.REACT_APP_AIR_API_BASE_URL;

/**
 * @name REACT_APP_SEARCH_ZIP_CODE_POST_URL
 * @const {string}
 *
 * @description It's the URL for posting zip code when user enters postal code in Search page and Form page.
 * Upon click on Submit postal zip code in Search results card, It will make network request to this env. var.
 * @example REACT_APP_SEARCH_ZIP_CODE_POST_URL = http://httpbin.org/get
 */
export const SEARCH_ZIP_CODE_POST_URL =
  process.env.REACT_APP_SEARCH_ZIP_CODE_POST_URL;

/**
 * @name REACT_APP_SEARCH_PG_DROPDOWN_MAX_HT
 * @const {number}
 *
 * @description It's maximum height in px of the Ports dropdown layout in Search bar page.
 * Dropdown layout appears upon hover over the origin/destination of shipment input fields.
 * @example REACT_APP_SEARCH_PG_DROPDOWN_MAX_HT = 500
 */
export const SEARCH_PG_DROPDOWN_MAX_HT =
  process.env.REACT_APP_SEARCH_PG_DROPDOWN_MAX_HT;

/**
 * @name REACT_APP_SEARCH_ZIP_CODE_TIMEOUT_MIN
 * @const {number}
 *
 * @description axios timeout in minutes for making [zip code]{@link module:envVars~REACT_APP_SEARCH_ZIP_CODE_POST_URL} post request.
 * @example REACT_APP_SEARCH_ZIP_CODE_TIMEOUT_MIN = 4
 */
export const SEARCH_ZIP_CODE_TIMEOUT_MIN =
  process.env.REACT_APP_SEARCH_ZIP_CODE_TIMEOUT_MIN;

/**
 * @name REACT_APP_SEARCH_SAILING_SCHEDULE_TIMEOUT_MIN
 * @const {number}
 *
 * @description axios timeout in minutes for making [sailing schedule]{@link module:envVars~REACT_APP_SAILING_SCHEDULE_POST_URL} post request.
 * @example REACT_APP_SEARCH_SAILING_SCHEDULE_TIMEOUT_MIN = 4
 */
export const SEARCH_SAILING_SCHEDULE_TIMEOUT_MIN =
  process.env.REACT_APP_SEARCH_SAILING_SCHEDULE_TIMEOUT_MIN;

/**
 * @name REACT_APP_SENTRY_DSN
 * @const {string}

 * @description It's the Sentry DSN for issue tracking in production.
 * @example REACT_APP_SENTRY_DSN = https://xyz767@sentry.io/xyz
 */
export const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
// export const G_TRACKING_ID = process.env.REACT_APP_G_TRACKING_ID;

/**
 * @name REACT_APP_SEARCH_BLUR_CARDS_EMAIL_LABEL_TEXT
 * @const {string}

 * @description It's the Email label in the overlay of Blur cards.
 * @example REACT_APP_SEARCH_BLUR_CARDS_EMAIL_LABEL_TEXT = Enter your email to unlock cards
 */
export const SEARCH_BLUR_CARDS_EMAIL_LABEL_TEXT =
  process.env.REACT_APP_SEARCH_BLUR_CARDS_EMAIL_LABEL_TEXT;

/**
 * @name REACT_APP_USER_EMAIL_BLUR_CARDS_POST_URL
 * @const {string}

 * @description Send email address of user in blur cards overlay to this env. var endpoint.
 * @example REACT_APP_USER_EMAIL_BLUR_CARDS_POST_URL = http://httpbin.org/post
 */
export const USER_EMAIL_BLUR_CARDS_POST_URL =
  process.env.REACT_APP_USER_EMAIL_BLUR_CARDS_POST_URL;

/**
 * @name REACT_APP_SEARCH_SIDEBAR_WEIGHT_VOLUME_LEARN_MORE_LINK
 * @const {string}

 * @description It's a link to open in new tab upon click on "Learn more" link text for weight/volume in Total shipment in sidebar.
 * @example REACT_APP_SEARCH_SIDEBAR_WEIGHT_VOLUME_LEARN_MORE_LINK = http://httpbin.org/post
 */
export const SEARCH_SIDEBAR_WEIGHT_VOLUME_LEARN_MORE_LINK =
  process.env.REACT_APP_SEARCH_SIDEBAR_WEIGHT_VOLUME_LEARN_MORE_LINK;

/**
 * @name REACT_APP_SEARCH_SIDEBAR_SHIPMENT_ROLES_LEARN_MORE_LINK
 * @const {string}

 * @description It's a link to open in new tab upon click on "Learn more" link text for shipment roles in sidebar.
 * @example REACT_APP_SEARCH_SIDEBAR_SHIPMENT_ROLES_LEARN_MORE_LINK = http://httpbin.org/post
 */
export const SEARCH_SIDEBAR_SHIPMENT_ROLES_LEARN_MORE_LINK =
  process.env.REACT_APP_SEARCH_SIDEBAR_SHIPMENT_ROLES_LEARN_MORE_LINK;

/**
 * @name REACT_APP_SHOW_SEARCH_SIDEBAR_SHIPMENT_ROLES_LEARN_MORE_LINK
 * @const {string}
 * @default NO

 * @description Whether to show or hide "Learn more" link text for shipment roles in sidebar. Either use "YES" or "NO".
 * @example REACT_APP_SHOW_SEARCH_SIDEBAR_SHIPMENT_ROLES_LEARN_MORE_LINK = YES
 */
export const SHOW_SEARCH_SIDEBAR_SHIPMENT_ROLES_LEARN_MORE_LINK =
  process.env.REACT_APP_SHOW_SEARCH_SIDEBAR_SHIPMENT_ROLES_LEARN_MORE_LINK.toLowerCase() ===
  'yes';

/**
 * @name REACT_APP_SEARCH_SIDEBAR_SERVICE_INCOTERM_LEARN_MORE_LINK
 * @const {string}

 * @description It's a link to open in new tab upon click on "Learn more" link text for service incoterm in sidebar.
 * @example REACT_APP_SEARCH_SIDEBAR_SERVICE_INCOTERM_LEARN_MORE_LINK = http://httpbin.org/post
 */
export const SEARCH_SIDEBAR_SERVICE_INCOTERM_LEARN_MORE_LINK =
  process.env.REACT_APP_SEARCH_SIDEBAR_SERVICE_INCOTERM_LEARN_MORE_LINK;

/**
 * @name REACT_APP_SHOW_SEARCH_SIDEBAR_SERVICE_INCOTERM_LEARN_MORE_LINK
 * @const {string}
 * @default NO

 * @description Whether to show or hide "Learn more" link text for service incoterm in sidebar. Either use "YES" or "NO".
 * @example REACT_APP_SHOW_SEARCH_SIDEBAR_SERVICE_INCOTERM_LEARN_MORE_LINK = YES
 */
export const SHOW_SEARCH_SIDEBAR_SERVICE_INCOTERM_LEARN_MORE_LINK =
  process.env.REACT_APP_SHOW_SEARCH_SIDEBAR_SERVICE_INCOTERM_LEARN_MORE_LINK.toLowerCase() ===
  'yes';

//*********************** Form ***********************

/**
 * @name REACT_APP_FORM_SUBMIT_URL
 * @const {string}

 * @description It's the URL for Form page and Upon click on "Search & Book", it makes network request to this env. var.
 * @example REACT_APP_FORM_SUBMIT_URL = http://httpbin.org/get
 */
export const FORM_SUBMIT_URL = process.env.REACT_APP_FORM_SUBMIT_URL;


export const FCL_DELIVERY_FEE= process.env.FCL_DELIVERY_FEE
export const LCL_DELIVERY_FEE= process.env.LCL_DELIVERY_FEE


/**
 * @name REACT_APP_FORM_PORT_PICKUP_URL
 * @const {string}

 * @description It's the URL to get pickup ports json on Form page.
 * @example REACT_APP_FORM_PORT_PICKUP_URL = http://httpbin.org/get
 */
export const FORM_PORT_PICKUP_URL = process.env.REACT_APP_FORM_PORT_PICKUP_URL;

/**
 * @name REACT_APP_FORM_PICKUP_FACTORY_LIST
 * @const {string}

 * @description It's the URL to get pickup existing factory list json on Form page.
 * @example REACT_APP_FORM_PICKUP_FACTORY_LIST = http://httpbin.org/get
 */
export const FORM_PICKUP_FACTORY_LIST =
  process.env.REACT_APP_FORM_PICKUP_FACTORY_LIST;

/**
 * @name REACT_APP_FORM_PORT_DELIVERY_URL
 * @const {string}

 * @description It's the URL to get delivery ports json on Form page.
 * @example REACT_APP_FORM_PORT_DELIVERY_URL = http://httpbin.org/get
 */
export const FORM_PORT_DELIVERY_URL =
  process.env.REACT_APP_FORM_PORT_DELIVERY_URL;

/**
 * @name REACT_APP_FORM_DELIVERY_FACTORY_LIST
 * @const {string}

 * @description It's the URL to get delivery existing factory list json on Form page.
 * @example REACT_APP_FORM_DELIVERY_FACTORY_LIST = http://httpbin.org/get
 */
export const FORM_DELIVERY_FACTORY_LIST =
  process.env.REACT_APP_FORM_DELIVERY_FACTORY_LIST;

/**
 * @name REACT_APP_FORM_DELIVERY_ECOMMERCE_URL
 * @const {string}

 * @description It's the URL to get E-commerce json in delivery destination location type on form page
 * @example REACT_APP_FORM_DELIVERY_ECOMMERCE_URL = http://httpbin.org/get
 */
export const FORM_DELIVERY_ECOMMERCE_URL =
  process.env.REACT_APP_FORM_DELIVERY_ECOMMERCE_URL;

/**
 * @name REACT_APP_FORM_DELIVERY_ECOMMERCE_LEARN_MORE_URL
 * @const {string}

 * @description It's the URL for "Learn more" text link in E-commerce dropdown in delivery destination location type on form page
 * @example REACT_APP_FORM_DELIVERY_ECOMMERCE_LEARN_MORE_URL = https://google.com
 */
export const FORM_DELIVERY_ECOMMERCE_LEARN_MORE_URL =
  process.env.REACT_APP_FORM_DELIVERY_ECOMMERCE_LEARN_MORE_URL;

/**
 * @name REACT_APP_FORM_DELIVERY_AMAZON_URL
 * @const {string}

 * @description It's the URL to get Amazon json in delivery destination location type on form page
 * @example REACT_APP_FORM_DELIVERY_AMAZON_URL = http://httpbin.org/get
 */
export const FORM_DELIVERY_AMAZON_URL =
  process.env.REACT_APP_FORM_DELIVERY_AMAZON_URL;

/**
 * @name REACT_APP_FORM_DELIVERY_AMAZON_DEFAULT_BOX_SELECTION
 * @const {number}

 * @description Amazon default radio box selection in Amazon warehouse delivery section on form page.
  Use "1" for "My Supplier/Me" selection, Use "2" for "Prime Freight" selection.
  Leave it empty for selecting none of them.
 * @example REACT_APP_FORM_DELIVERY_AMAZON_DEFAULT_BOX_SELECTION = 1
   @example REACT_APP_FORM_DELIVERY_AMAZON_DEFAULT_BOX_SELECTION =
 */
export const FORM_DELIVERY_AMAZON_DEFAULT_BOX_SELECTION =
  process.env.REACT_APP_FORM_DELIVERY_AMAZON_DEFAULT_BOX_SELECTION;

/**
 * @name REACT_APP_FORM_DELIVERY_AMAZON_DEFAULT_PALLET_SELECTION
 * @const {number}

 * @description Amazon default radio pallet selection in Amazon warehouse delivery section on form page.
  Use "1" for "My Supplier/Me" selection, Use "2" for "Prime Freight" selection,
  Use "3" for "Not Applicable" selection.  Leave it empty for selecting none of them.
 * @example REACT_APP_FORM_DELIVERY_AMAZON_DEFAULT_PALLET_SELECTION = 3
   @example REACT_APP_FORM_DELIVERY_AMAZON_DEFAULT_PALLET_SELECTION =
 */
export const FORM_DELIVERY_AMAZON_DEFAULT_PALLET_SELECTION =
  process.env.REACT_APP_FORM_DELIVERY_AMAZON_DEFAULT_PALLET_SELECTION;

/**
 * @name REACT_APP_FORM_INIT_PICKUP_DELIVERY_URL
 * @const {string}

 * @description It's the URL to make network request to get the initial/default pickup/delivery json on form page
 * @example REACT_APP_FORM_INIT_PICKUP_DELIVERY_URL = http://httpbin.org/get
 */
export const FORM_INIT_PICKUP_DELIVERY_URL =
  process.env.REACT_APP_FORM_INIT_PICKUP_DELIVERY_URL;

/**
 * @name REACT_APP_G_RECAPTCHA_SITE_KEY
 * @const {string}

 * @description It's Google invisible reCaptcha site key
 * @example REACT_APP_G_RECAPTCHA_SITE_KEY = 6Lffgfggfrtr8AtW
 */
export const G_RECAPTCHA_SITE_KEY = process.env.REACT_APP_G_RECAPTCHA_SITE_KEY;

/**
 * @name REACT_APP_SHOW_G_RECAPTCHA
 * @const {string}

 * @description Whether to make google invisible recaptcha visible
 * @example REACT_APP_SHOW_G_RECAPTCHA = NO
 */
export const SHOW_G_RECAPTCHA = process.env.REACT_APP_SHOW_G_RECAPTCHA;

/**
 * @name REACT_APP_LANDING_PAGE_SEARCH_CLICK_URL
 * @const {string}

 * @description On Landing pge (i.e. `domain-name.com/landing-page`), Upon click on "Search" button,
   the app will redirect to this env. var with params: <br><br>
    `this env. var + params` = `https://www.primefreight.com/search/?type=api-fcl&from=...&to=...`
 * @example REACT_APP_LANDING_PAGE_SEARCH_CLICK_URL = https://www.primefreight.com/search/
 */
export const LANDING_PAGE_SEARCH_CLICK_URL =
  process.env.REACT_APP_LANDING_PAGE_SEARCH_CLICK_URL;

/**
 * @name REACT_APP_LANDING_PG_DROPDOWN_MAX_HT
 * @const {number}

 * @description It's maximum height in px of the Ports dropdown layout in Landing page.
 * Dropdown layout appears upon hover over the origin/destination of shipment input fields.
 * @example REACT_APP_LANDING_PG_DROPDOWN_MAX_HT = 400
 */
export const LANDING_PG_DROPDOWN_MAX_HT =
  process.env.REACT_APP_LANDING_PG_DROPDOWN_MAX_HT;

/**
 * @name REACT_APP_SEARCH_PG_AUTOCOMPLETE_MAX_DROPDOWN
 * @const {number}

 * @description It's the maximum number of the Ports (city, country) in autocomplete mode, that appears in dropdown layout in Search bar page.
 * Dropdown layout appears upon hover over the origin/destination of shipment input fields.
 * @example REACT_APP_SEARCH_PG_AUTOCOMPLETE_MAX_DROPDOWN = 7
 */
export const SEARCH_PG_AUTOCOMPLETE_MAX_DROPDOWN =
  process.env.REACT_APP_SEARCH_PG_AUTOCOMPLETE_MAX_DROPDOWN;

/**
 * @name REACT_APP_LANDING_PG_AUTOCOMPLETE_MAX_DROPDOWN
 * @const {number}

 * @description It's the maximum number of the Ports (city, country) in autocomplete mode, that appears in dropdown layout in Landing page.
 * Dropdown layout appears upon hover over the origin/destination of shipment input fields.
 * @example REACT_APP_LANDING_PG_AUTOCOMPLETE_MAX_DROPDOWN = 3
 */
export const LANDING_PG_AUTOCOMPLETE_MAX_DROPDOWN =
  process.env.REACT_APP_LANDING_PG_AUTOCOMPLETE_MAX_DROPDOWN;

/**
 * @name REACT_APP_NEW_RATES_BY_UUID_GET_URL
 * @const {string}

 * @description It's the URL to get new rate json by UUID
 * @example REACT_APP_NEW_RATES_BY_UUID_GET_URL = http://httpbin.org/get
 */
export const NEW_RATES_BY_UUID_GET_URL =
  process.env.REACT_APP_NEW_RATES_BY_UUID_GET_URL;

/**
 * @name REACT_APP_SHOW_CONTACT_FORM_AFTER_UUID_PC_TIME_PASSED
 * @const {number}

 * @description It is for showing "contact form" in case of no new rates appears by [uuid]{@link module:envVars~REACT_APP_NEW_RATES_BY_UUID_GET_URL},
   after completion of this env. var percentage of time since making the first network request by [uuid]{@link module:envVars~REACT_APP_NEW_RATES_BY_UUID_GET_URL}.
 * @example REACT_APP_SHOW_CONTACT_FORM_AFTER_UUID_PC_TIME_PASSED = 50
 */
export const SHOW_CONTACT_FORM_AFTER_UUID_PC_TIME_PASSED =
  process.env.REACT_APP_SHOW_CONTACT_FORM_AFTER_UUID_PC_TIME_PASSED;

/**
 * @name REACT_APP_DASHBOARD_POST_URL
 * @const {string}

 * @description It's the URL to get the initial dashboard json
 * @example REACT_APP_DASHBOARD_POST_URL = http://httpbin.org/post
 */
export const DASHBOARD_POST_URL = process.env.REACT_APP_DASHBOARD_POST_URL;

/**
 * @name REACT_APP_DASHBOARD_SHIPMENT_LINK
 * @const {string}

 * @description It's the URL to open a new tab upon click on single Shipment item reference number on Dashboard page.
 * @example REACT_APP_DASHBOARD_SHIPMENT_LINK = http://httpbin.org/open-in-new-tab
 */
export const DASHBOARD_SHIPMENT_LINK =
  process.env.REACT_APP_DASHBOARD_SHIPMENT_LINK;

/**
 * @name REACT_APP_DASHBOARD_BOOKING_LINK
 * @const {string}

 * @description It's the URL to open a new tab upon click on single Booking item booking id on Dashboard page.
 * @example REACT_APP_DASHBOARD_BOOKING_LINK = http://httpbin.org/open-in-new-tab
 */
export const DASHBOARD_BOOKING_LINK =
  process.env.REACT_APP_DASHBOARD_BOOKING_LINK;

//don't export these env. vars, these are directly used in reducers, other components, etc.

/**
 * @name REACT_APP_ALLOWED_PARENT_DOMAINS
 * @const {string}

 * @description Parent website domain names. These domains are allowed to send messages to this web app.
 * @example REACT_APP_ALLOWED_PARENT_DOMAINS = http://primefreight.com/,http://primefreight-2.com
 */
const ALLOWED_PARENT_DOMAINS = process.env.REACT_APP_ALLOWED_PARENT_DOMAINS;

/**
 * @name REACT_APP_SEARCH_CONTACT_FORM_HEADER
 * @const {string}

 * @description Header text for Contact form above all result cards on search page.
 * @example REACT_APP_SEARCH_CONTACT_FORM_HEADER = Header text.
 */
const SEARCH_CONTACT_FORM_HEADER =
  process.env.REACT_APP_SEARCH_CONTACT_FORM_HEADER;

/**
 * @name REACT_APP_SEARCH_CONTACT_FORM_SUB_HEADER
 * @const {string}

 * @description Sub Header text description for Contact form above all result cards on search page.
 * @example REACT_APP_SEARCH_CONTACT_FORM_SUB_HEADER = Sub text with description..
 */
const SEARCH_CONTACT_FORM_SUB_HEADER =
  process.env.REACT_APP_SEARCH_CONTACT_FORM_SUB_HEADER;

/**
 * @name REACT_APP_SHOW_OPEN_MAP
 * @const {string}

 * @description Whether to show google map on result card beside view-details section. Either use "YES" or "NO"
 * @example REACT_APP_SHOW_OPEN_MAP = YES
 */
const SHOW_OPEN_MAP = process.env.REACT_APP_SHOW_OPEN_MAP;

/**
 * @name REACT_APP_PRIMARY_COLOR
 * @const {string}

 * @description Primary color of the web app.
 * @example REACT_APP_PRIMARY_COLOR = #311ac4
 */
const PRIMARY_COLOR = process.env.REACT_APP_PRIMARY_COLOR;

/**
 * @name REACT_APP_SECONDARY_COLOR
 * @const {string}

 * @description Secondary color of the web app.
 * @example REACT_APP_SECONDARY_COLOR = #17154e
 */
const SECONDARY_COLOR = process.env.REACT_APP_SECONDARY_COLOR;

/**
 * @name REACT_APP_SEARCH_CARD_RATING_STAR_COLOR
 * @const {string}

 * @description  Star rating color on company name section on a result card.
 * @example REACT_APP_SEARCH_CARD_RATING_STAR_COLOR = green
 */
const SEARCH_CARD_RATING_STAR_COLOR =
  process.env.REACT_APP_SEARCH_CARD_RATING_STAR_COLOR;

/**
 * @name REACT_APP_SEARCH_SHOW_PORT_SWITCHER
 * @const {string}

 * @description  Whether to show port switcher button between origin shipment and destination shipment input field on search page.  Either use "YES" or "NO"
 * @example REACT_APP_SEARCH_SHOW_PORT_SWITCHER = YES
 */
const SEARCH_SHOW_PORT_SWITCHER =
  process.env.REACT_APP_SEARCH_SHOW_PORT_SWITCHER;

/**
 * @name REACT_APP_SEARCH_CARD_ESTIMATE_TEXT
 * @const {string}

 * @description  Estimate Tooltip text on a estimate type result card on search page.
 * @example REACT_APP_SEARCH_CARD_ESTIMATE_TEXT = It is an estimate card. Some desc about estimate, etc
 */
const SEARCH_CARD_ESTIMATE_TEXT =
  process.env.REACT_APP_SEARCH_CARD_ESTIMATE_TEXT;

/**
 * @name REACT_APP_SEARCH_CARD_VIEW_DETAILS_REVIEW_TEXT
 * @const {string}

 * @description Reviews text to be shown on view-details before booking if user has not viewed it before. Do not show it if card is of variable rate type
 * @example REACT_APP_SEARCH_CARD_VIEW_DETAILS_REVIEW_TEXT = Please review shipment details and options before proceeding
 */
const SEARCH_CARD_VIEW_DETAILS_REVIEW_TEXT =
  process.env.REACT_APP_SEARCH_CARD_VIEW_DETAILS_REVIEW_TEXT;

/**
 * @name REACT_APP_SEARCH_DATE_PICKER_DAYS_PAST
 * @const {number}

 * @description DatePicker past dates limit on search page
 * @example REACT_APP_SEARCH_DATE_PICKER_DAYS_PAST = 5
 */
const SEARCH_DATE_PICKER_DAYS_PAST =
  process.env.REACT_APP_SEARCH_DATE_PICKER_DAYS_PAST;

/**
 * @name REACT_APP_SEARCH_DATE_PICKER_DAYS_FUTURE
 * @const {number}

 * @description DatePicker future dates limit on search page
 * @example REACT_APP_SEARCH_DATE_PICKER_DAYS_FUTURE = Infinity
 */
const SEARCH_DATE_PICKER_DAYS_FUTURE =
  process.env.REACT_APP_SEARCH_DATE_PICKER_DAYS_FUTURE;

/**
 * @name REACT_APP_SEARCH_CARD_VARIABLE_RATE_TEXT
 * @const {string}

 * @description Popover text for variable rate type card on search page
 * @example REACT_APP_SEARCH_CARD_VARIABLE_RATE_TEXT = it is a variable type card, etc
 */
const SEARCH_CARD_VARIABLE_RATE_TEXT =
  process.env.REACT_APP_SEARCH_CARD_VARIABLE_RATE_TEXT;

/**
 * @name REACT_APP_CUSTOM_SERVICE_USER_DEFAULT_COMMODITY_NO
 * @const {number}

 * @description It's the number of commodity for custom service section on form page.
 * @example REACT_APP_CUSTOM_SERVICE_USER_DEFAULT_COMMODITY_NO = 2
 */
const CUSTOM_SERVICE_USER_DEFAULT_COMMODITY_NO =
  process.env.REACT_APP_CUSTOM_SERVICE_USER_DEFAULT_COMMODITY_NO;

/**
 * @name REACT_APP_FORM_DELIVERY_ECOMMERCE_DESC_TEXT
 * @const {string}

 * @description Delivery destination location type, e-commerce description text on form page.
 * @example REACT_APP_FORM_DELIVERY_ECOMMERCE_DESC_TEXT = env. var text desc about ecommerce mode
 */
const FORM_DELIVERY_ECOMMERCE_DESC_TEXT =
  process.env.REACT_APP_FORM_DELIVERY_ECOMMERCE_DESC_TEXT;

/**
 * @name REACT_APP_FORM_DELIVERY_SHOW_ECOMMERCE
 * @const {string}

 * @description Whether to show e-commerce option in delivery destination location type on form page. Either use "YES" or "NO".
 * @example REACT_APP_FORM_DELIVERY_SHOW_ECOMMERCE = YES
 */
const FORM_DELIVERY_SHOW_ECOMMERCE =
  process.env.REACT_APP_FORM_DELIVERY_SHOW_ECOMMERCE;

/**
 * @name REACT_APP_FORM_DELIVERY_AMAZON_DESC_TEXT
 * @const {string}

 * @description Delivery destination location type, amazon warehouse description text on form page.
 * @example REACT_APP_FORM_DELIVERY_AMAZON_DESC_TEXT = env. var text desc about amazon warehouse mode
 */
const FORM_DELIVERY_AMAZON_DESC_TEXT =
  process.env.REACT_APP_FORM_DELIVERY_AMAZON_DESC_TEXT;

/**
 * @name REACT_APP_FORM_DELIVERY_SHOW_AMAZON
 * @const {string}

 * @description Whether to show amazon option in delivery destination location type on form page. Either use "YES" or "NO".
 * @example REACT_APP_FORM_DELIVERY_SHOW_AMAZON = YES
 */
const FORM_DELIVERY_SHOW_AMAZON =
  process.env.REACT_APP_FORM_DELIVERY_SHOW_AMAZON;

/**
 * @name REACT_APP_LANDING_PAGE_TEXT_INPUT_COLOR
 * @const {string}

 * @description Input text field color on Landing page.
 * @example REACT_APP_LANDING_PAGE_TEXT_INPUT_COLOR = rgba(255,0,0,1.0)
 */
const LANDING_PAGE_TEXT_INPUT_COLOR =
  process.env.REACT_APP_LANDING_PAGE_TEXT_INPUT_COLOR;

/**
 * @name REACT_APP_FORM_SHOW_CUSTOM_SERVICE
 * @const {string}

 * @description Whether to show Custom service on form page. Either use "YES" or "NO".
 * @example REACT_APP_FORM_SHOW_CUSTOM_SERVICE = YES
 */
const FORM_SHOW_CUSTOM_SERVICE = process.env.REACT_APP_FORM_SHOW_CUSTOM_SERVICE;

/**
 * @name REACT_APP_FORM_INCLUDE_ORIGIN_CHARGE
 * @const {string}

 * @description Whether to make checkbox checked by default in pickup section on form page. Either use "YES" or "NO".
 * @example REACT_APP_FORM_INCLUDE_ORIGIN_CHARGE = YES
 */
const FORM_INCLUDE_ORIGIN_CHARGE =
  process.env.REACT_APP_FORM_INCLUDE_ORIGIN_CHARGE;

/**
 * @name REACT_APP_FORM_INCLUDE_DESTINATION_CHARGE
 * @const {string}

 * @description Whether to make checkbox checked by default in delivery section on form page. Either use "YES" or "NO".
 * @example REACT_APP_FORM_INCLUDE_DESTINATION_CHARGE = YES
 */
const FORM_INCLUDE_DESTINATION_CHARGE =
  process.env.REACT_APP_FORM_INCLUDE_DESTINATION_CHARGE;

/**
 * @name REACT_APP_FORM_DATE_PICKER_DAYS_PAST
 * @const {number}

 * @description DatePicker past dates limit on form page.
 * @example REACT_APP_FORM_DATE_PICKER_DAYS_PAST = 5
 */
const FORM_DATE_PICKER_DAYS_PAST =
  process.env.REACT_APP_FORM_DATE_PICKER_DAYS_PAST;

/**
 * @name REACT_APP_FORM_DATE_PICKER_DAYS_FUTURE
 * @const {number}

 * @description DatePicker future dates limit on form page.
 * @example REACT_APP_FORM_DATE_PICKER_DAYS_FUTURE = 5
 */
const FORM_DATE_PICKER_DAYS_FUTURE =
  process.env.REACT_APP_FORM_DATE_PICKER_DAYS_FUTURE;
